import React, { useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import Fab from '@mui/material/Fab';
import MenuIcon from '@mui/icons-material/Menu';
import Drawer from '@mui/material/Drawer';

// Components
import { getConsultation, getConsultationNote, updateConsultationNote } from '../../../routes/doctor/consultation';
import { setConsultationId, setCurrentTime, setIsConsent, setIsQrCodePopupOpened, setIsRefreshContent, setLanguage, setSecurityPin } from '../../../redux/features/doctor/Dashboard/consultation/consultation';
import Sidebar from './sidebar/Sidebar';
import moment from 'moment';
import Note from './note/Note';
import { setAudioTranscription, setDescription, setInfo, setIsGenerateNewDescription, setIsMicrophoneDisabled, setIsRecorderBarDisabled, setIsRefreshConsultationNote, setTitle, setAdditionalTranscription, resetConsultationNote, DefaultPatientInfoLabels } from '../../../redux/features/doctor/Dashboard/consultation/consultationNote';
import { setNotificationMessage } from '../../../redux/features/general/notification';
import { getDoctorPreferences } from '../../../routes/doctor/patients';
import { setIsSuspendedGroupPopupOpened } from '../../../redux/features/general/navigation';

const Container: React.FC = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { consultationId: paramConsultationId, consultationNoteId: paramConsultationNoteId } = useParams<{ consultationId: string, consultationNoteId?: string }>();

    const colors = useAppSelector(state => state.theme.colors);
    const reduxConsultationId = useAppSelector(state => state.consultation.consultationId);
    const securityPin = useAppSelector(state => state.consultation.securityPin);
    const info = useAppSelector(state => state.consultationNote.info);
    const isRecording = useAppSelector(state => state.consultationNote.isRecording);
    const isRefreshConsultationNote = useAppSelector(state => state.consultationNote.isRefreshConsultationNote);
    const isQrCodePopupOpened = useAppSelector(state => state.consultation.isQrCodePopupOpened);

    const [timeLeft, setTimeLeft] = useState(10);
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
    const [currentConsultationId, setCurrentConsultationId] = useState<string | undefined>(paramConsultationId);
    const [currentConsultationNoteId, setCurrentConsultationNoteId] = useState<string | undefined>(paramConsultationNoteId);

    const isMobile = useMediaQuery('(orientation: portrait)');
    const initialLoadDoneRef = useRef(false);


    useEffect(() => {
        setCurrentConsultationId(paramConsultationId);
        // Reset note state if consultationId changes to ensure clean slate for new consultation context
        if (paramConsultationId && paramConsultationId !== reduxConsultationId) {
            const translatedLabels: DefaultPatientInfoLabels = {
                name: t('pdf.label.name', 'Name:'),
                dob: t('pdf.label.dob', 'DOB:'),
                healthCard: t('pdf.label.healthCard', 'Health Card:'),
            };
            dispatch(resetConsultationNote({ labels: translatedLabels })); // Reset note details if main consultation ID changes
        }
    }, [paramConsultationId, dispatch, reduxConsultationId, t]);

    useEffect(() => {
        setCurrentConsultationNoteId(paramConsultationNoteId);
    }, [paramConsultationNoteId]);


    window.onbeforeunload = function (e: BeforeUnloadEvent) {
      if (isRecording) {
        if (currentConsultationNoteId && securityPin) {
            updateConsultationNote({
                consultationNoteId: currentConsultationNoteId,
                securityPin,
                stopRecording: true,
            });
        }
        e.preventDefault(); 
        e.returnValue = ''; 
        return ''; 
      }
    };

    const handleConsultation = async (id?: string) => {
      const effectiveConsultationId = id || currentConsultationId;
      if (!effectiveConsultationId) {
        console.warn("handleConsultation called without a valid consultationId.");
        if (!initialLoadDoneRef.current) navigate('/dashboard');
        return;
      }

      try {
        const res = await getConsultation(effectiveConsultationId) as any;
        if (res?.data?.status === 200) {
          if (res.data.consultation.status === 'pending') {
            const { consultation } = res.data;
            if (consultation) {
              dispatch(setConsultationId(consultation._id));
              dispatch(setIsConsent(consultation.isConsent));
              dispatch(setLanguage(consultation.language));
              dispatch(setSecurityPin(consultation.securityPin));
            }
            if (res.data.isAccessRequested) {
              dispatch(setIsQrCodePopupOpened(true));
            }
            if (res.data.isDeviceConnected && isQrCodePopupOpened) {
              dispatch(setNotificationMessage(t('deviceConnected')));
              dispatch(setIsQrCodePopupOpened(false));
            }
          } else {
            dispatch(setNotificationMessage(t('consultationNotPendingOrAccessDenied')));
            navigate('/dashboard');
          }
        } else {
          if (res.data.error == 'suspendedGroup') { 
            dispatch(setIsSuspendedGroupPopupOpened(true)); 
            navigate('/dashboard'); 
          } else { 
            dispatch(setNotificationMessage(t('errorFetchingConsultation')));
            if (!initialLoadDoneRef.current || !reduxConsultationId) navigate('/dashboard');
          }; 
        }
      } catch (error) {
        console.error("Error in handleConsultation:", error);
        dispatch(setNotificationMessage(t('errorFetchingConsultation')));
        if (!initialLoadDoneRef.current || !reduxConsultationId) navigate('/dashboard');
      }
    };

    const handleConsultationNote = async (noteId?: string) => {
      const effectiveNoteId = noteId || currentConsultationNoteId;
      if (!effectiveNoteId || !securityPin || !currentConsultationId) {
        console.warn("handleConsultationNote called without a valid noteId, securityPin, or consultationId.");
        return;
      }

      try {
        const res = await getConsultationNote(effectiveNoteId) as any;
        if (res?.data?.status === 200) {
          const { consultationNote } = res.data;
          if (!info || consultationNote?.isRefresh || info?._id !== consultationNote?._id) {
            dispatch(setInfo(consultationNote));
            dispatch(setAudioTranscription(consultationNote.audioTranscription || ''));
            dispatch(setDescription(consultationNote.description || ''));
            dispatch(setTitle(consultationNote.title || ''));
            dispatch(setAdditionalTranscription(consultationNote.additionalTranscription || ''));

            if (consultationNote.isRefresh) {
              dispatch(setIsGenerateNewDescription(true));
              updateConsultationNote({
                consultationNoteId: effectiveNoteId,
                securityPin: securityPin,
                removeRefresh: true
              });
            }
            updateConsultationNote({
                consultationNoteId: effectiveNoteId,
                securityPin,
                stopRecording: true,
            });
          }

          if ((consultationNote?.isRecording) && (consultationNote?.micType === 'mobile')) {
            dispatch(setIsMicrophoneDisabled(true));
          } else {
            dispatch(setIsMicrophoneDisabled(false));
          }
        } else {
          if (res.data.error == 'suspendedGroup') { 
            dispatch(setIsSuspendedGroupPopupOpened(true)); 
            navigate('/dashboard'); 
          } else { 
            if (paramConsultationNoteId) {
              dispatch(setNotificationMessage(t('errorFetchingConsultationNote')));
            }
            console.warn("Failed to fetch consultation note or note does not exist:", res?.data?.error || "Unknown error");
          }; 
        }; 
      } catch (error) {
        console.error("Error in handleConsultationNote:", error);
        if (paramConsultationNoteId) {
             dispatch(setNotificationMessage(t('errorFetchingConsultationNote')));
        }
      }
    };

    const handleDoctorPreferences = async () => {
      try {
        const res = await getDoctorPreferences() as any;
        if (res?.data.status === 200) {
          if (res.data.doctorPreferences?.consultation) {
            const { consultation } = res.data.doctorPreferences;
            dispatch(setIsRecorderBarDisabled(consultation.isRecorderBarDisabled));
          }
        }
      } catch (error) {
        console.error("Error fetching doctor preferences:", error);
      }
    };

    useEffect(() => {
        if (currentConsultationId) {
            handleConsultation(currentConsultationId).then(() => {
                if (currentConsultationNoteId && securityPin) {
                    handleConsultationNote(currentConsultationNoteId);
                }
            });
            handleDoctorPreferences();
            initialLoadDoneRef.current = true;
        } else {
            navigate('/dashboard');
        }
    }, [currentConsultationId]);

    useEffect(() => {
        if (currentConsultationNoteId && securityPin && initialLoadDoneRef.current) {
            if (info?._id !== currentConsultationNoteId) {
                 handleConsultationNote(currentConsultationNoteId);
            }
        }
    }, [currentConsultationNoteId, securityPin]);


    useEffect(() => {
        let myInterval = setInterval(() => {
            if (timeLeft > 0) {
                setTimeLeft(prevTime => prevTime - 1);
            } else {
                if (currentConsultationId && securityPin) {
                    dispatch(setCurrentTime(moment().utc(true).format('LLLL')));
                    handleConsultation(currentConsultationId);
                    if (currentConsultationNoteId) {
                        handleConsultationNote(currentConsultationNoteId);
                    }
                    handleDoctorPreferences();
                }
                setTimeLeft(10);
            }
        }, 1000);

        return () => {
            clearInterval(myInterval);
        };
    }, [timeLeft, currentConsultationId, currentConsultationNoteId, securityPin, dispatch]);

    useEffect(() => {
      if (isRefreshConsultationNote && currentConsultationNoteId && securityPin) {
        handleConsultationNote(currentConsultationNoteId);
        dispatch(setIsRefreshConsultationNote(false));
      }
    }, [isRefreshConsultationNote, currentConsultationNoteId, securityPin, dispatch]);


    const toggleMobileSidebar = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
        if (
          event.type === 'keydown' &&
          ((event as React.KeyboardEvent).key === 'Tab' ||
            (event as React.KeyboardEvent).key === 'Shift')
        ) {
          return;
        }
        setIsMobileSidebarOpen(open);
    };

    const handleMobileSidebarClose = () => {
        setIsMobileSidebarOpen(false);
    };

    if (!currentConsultationId && !initialLoadDoneRef.current) {
        return null;
    }

    return (
      <Box sx={{
          display: 'flex',
          flexDirection: 'row', 
          alignItems: 'flex-start', 
          width: '100vw',
          height: 'calc(100vh - 5vh)', 
          position: 'absolute',
          bottom: 0,
          backgroundColor: colors?.interface
      }}>
        {!isMobile && <Sidebar isMobile={false} />}
        {isMobile && (
            <Drawer
                anchor="left"
                open={isMobileSidebarOpen}
                onClose={toggleMobileSidebar(false)} 
                sx={{
                    zIndex: 10000001, 
                    '& .MuiDrawer-paper': {
                        width: '80vw', 
                        height: '100vh', 
                        backgroundColor: colors?.container, 
                        boxSizing: 'border-box',
                    },
                }}
            >
                <Sidebar isMobile={true} isOpen={isMobileSidebarOpen} onClose={handleMobileSidebarClose} />
            </Drawer>
        )}
        {currentConsultationId && <Note isMobile={isMobile} />}

        {isMobile && (
            <Fab
                color="primary"
                aria-label="open sidebar"
                onClick={toggleMobileSidebar(true)} 
                sx={{
                    position: 'fixed',
                    bottom: 16,
                    right: 16,
                    zIndex: 10000, 
                    backgroundColor: colors?.main,
                    '&:hover': {
                        backgroundColor: colors?.mainHover,
                    }
                }}
            >
                <MenuIcon sx={{ color: 'white' }} />
            </Fab>
        )}
      </Box>
  );
};

export default Container;